import { expenseApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { ServiceResponseI, DataI } from "../../interfaces";
import { ExpenseI, GetExpenseParamsI, ListExpenseI, OneExpenseForFrontI } from "@interfaces";
import { AxiosError } from "axios";

const getExpenses = async ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  dateFrom,
  dateTo,
}: GetExpenseParamsI): Promise<ServiceResponseI<DataI<ListExpenseI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page",    (pagination.pageIndex + 1).toString());
    params.append("limit",   pagination.pageSize.toString());
    params.append("search",  globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (branchId)  params.append("branchId",  branchId.toString());
    if (dateFrom)  params.append("dateFrom",  dateFrom);
    if (dateTo)    params.append("dateTo",    dateTo);

    const { data } = await expenseApi.get<ServiceResponseI<DataI<ListExpenseI>>>("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const expenseById = async (
  id: string,
): Promise<ServiceResponseI<OneExpenseForFrontI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await expenseApi.get<ServiceResponseI<OneExpenseForFrontI>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const expensePost = async <T>(
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await expenseApi.post<ServiceResponseI<string>>(
      "",
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const expensePut = async <T>(
  id: string,
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await expenseApi.put<ServiceResponseI<string>>(
      `/${id}`,
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const expenseDelete = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await expenseApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const expenseAnnullar = async <T>(
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await expenseApi.patch<ServiceResponseI<string>>(
      `/annullar/${id}`,
      {},
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useExpenses = ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  dateFrom,
  dateTo,
}: GetExpenseParamsI) => {
  const expensesQuery = useQuery(
    ["expenses", { pagination, sorting, globalFilter, branchId, dateFrom, dateTo }],
    () => getExpenses({ pagination, sorting, globalFilter, branchId, dateFrom, dateTo }),
    { refetchOnWindowFocus: false, keepPreviousData: true },
  );
  return { expensesQuery };
};